import { resolveBackendEndpoint, usesLocalBackend } from "./backend-endpoints";
export { resolveBackendEndpoint } from "./backend-endpoints";
import { z } from "zod";
import { contextSchema, type JourneyContext } from "@/journey/context";
import { validateDiscoveryModel, type ExecutableDiscoveryModel } from "@/domain/discovery-model";

export type BackendData = Record<string, unknown>;

export type BackendSuccess<TData extends BackendData = BackendData> = {
  ok: true;
  request_id: string;
  data: TData;
};

export type BackendFailure = {
  ok: false;
  request_id: string;
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
};

export type BackendEnvelope<TData extends BackendData = BackendData> =
  BackendSuccess<TData> | BackendFailure;

export type BackendOperation = "admin_management" | "initial_website_analysis" | "update_context" | "update_discovery" | "send_email";

const leadEmailResultSchema = z.object({
  outcome: z.enum(["accepted", "already_sent", "notifications_disabled", "ineligible"]),
});
export type LeadEmailResult = z.infer<typeof leadEmailResultSchema>;

const initialAnalysisIdentitySchema = z
  .object({
    customerId: z.string().min(1),
    normalizedDomain: z.string().min(1),
    websiteUrl: z.string().min(1),
    durationMs: z.number().int().nonnegative(),
  })
  .strict();

const readyAnalysisResultSchema = initialAnalysisIdentitySchema
  .extend({
    outcome: z.enum(["created", "existing"]),
    onboardingStatus: z.literal("ready"),
    publicSlug: z.string().min(1),
    // Optional for forward compatibility while the compact backend contract settles.
    businessName: z.string().min(1).optional(),
  })
  .strict();

const analysisInProgressResultSchema = initialAnalysisIdentitySchema
  .extend({
    outcome: z.literal("analysis_in_progress"),
    onboardingStatus: z.literal("analyzing"),
    qottaFit: z.boolean().nullable(),
    qottaFitReason: z.string(),
  })
  .strict();

const notRelevantAnalysisResultSchema = initialAnalysisIdentitySchema
  .extend({
    outcome: z.literal("not_relevant"),
    onboardingStatus: z.literal("not_relevant"),
    qottaFit: z.literal(false),
    qottaFitReason: z.string(),
  })
  .strict();

export const initialWebsiteAnalysisResultSchema = z.discriminatedUnion("outcome", [
  readyAnalysisResultSchema,
  analysisInProgressResultSchema,
  notRelevantAnalysisResultSchema,
]);

export type InitialWebsiteAnalysisResult = z.infer<typeof initialWebsiteAnalysisResultSchema>;
export type ReadyInitialWebsiteAnalysisResult = z.infer<typeof readyAnalysisResultSchema>;

export class BackendApiError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly requestId: string | null,
    public readonly status: number | null,
    public readonly details?: unknown,
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = "BackendApiError";
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseEnvelope<TData extends BackendData>(
  value: unknown,
  fallbackRequestId: string,
  status: number,
): BackendEnvelope<TData> {
  if (!isObject(value) || typeof value["ok"] !== "boolean") {
    throw new BackendApiError(
      "The Qotta backend returned an unexpected response.",
      "invalid_response",
      fallbackRequestId,
      status,
    );
  }

  const requestId =
    typeof value["request_id"] === "string" && value["request_id"]
      ? value["request_id"]
      : fallbackRequestId;

  if (value["ok"] === true) {
    if (!isObject(value["data"])) {
      throw new BackendApiError(
        "The Qotta backend returned an unexpected response.",
        "invalid_response",
        requestId,
        status,
      );
    }
    return { ok: true, request_id: requestId, data: value["data"] as TData };
  }

  const backendError = isObject(value["error"]) ? value["error"] : {};
  return {
    ok: false,
    request_id: requestId,
    error: {
      code: typeof backendError["code"] === "string" ? backendError["code"] : "backend_error",
      message:
        typeof backendError["message"] === "string"
          ? backendError["message"]
          : "The Qotta backend could not complete the request.",
      ...(backendError["details"] !== undefined ? { details: backendError["details"] } : {}),
    },
  };
}

// Canonical frontend request deadlines: 21 minutes for analysis, 26 minutes per update.
// Keep these explicit; do not restore the former 150-second update deadlines.
// Independent of backend OpenAI/function timeouts. Policy: docs/journey-workspace.md.
const operationTimeoutMs: Record<BackendOperation, number> = {
  initial_website_analysis: 1_260_000,
  update_context: 1_560_000,
  update_discovery: 1_560_000,
  send_email: 60_000,
  admin_management: 60_000,
};

export type BackendTrace = { traceId: string; customerId?: string };

async function postBackend<TData extends BackendData>(
  operation: BackendOperation,
  payload: Record<string, unknown>,
  trace?: BackendTrace,
  idToken?: string,
): Promise<BackendSuccess<TData>> {
  const endpoint = resolveBackendEndpoint(operation);
  const clientRequestId = crypto.randomUUID();
  const controller = new AbortController();
  const timeout = globalThis.setTimeout(() => controller.abort(), operationTimeoutMs[operation]);

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        "X-Request-ID": clientRequestId,
        "X-Qotta-Trace-Id": trace?.traceId ?? crypto.randomUUID(),
        ...(trace?.customerId ? { "X-Qotta-Customer-Id": trace.customerId } : {}),
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    const responseRequestId = response.headers.get("X-Request-ID") || clientRequestId;
    let parsed: unknown;
    try {
      parsed = JSON.parse(await response.text());
    } catch (error) {
      throw new BackendApiError(
        "The Qotta backend returned an unreadable response.",
        "invalid_response",
        responseRequestId,
        response.status,
        undefined,
        { cause: error },
      );
    }

    const envelope = parseEnvelope<TData>(parsed, responseRequestId, response.status);
    if (!response.ok || !envelope.ok) {
      const failure = envelope.ok ? null : envelope;
      throw new BackendApiError(
        failure?.error.message || `The Qotta backend returned HTTP ${response.status}.`,
        failure?.error.code || "http_error",
        failure?.request_id || responseRequestId,
        response.status,
        failure?.error.details,
      );
    }
    return envelope;
  } catch (error) {
    if (error instanceof BackendApiError) throw error;
    if (controller.signal.aborted) {
      throw new BackendApiError(
        "The Qotta backend took too long to respond. Please try again.",
        "timeout",
        clientRequestId,
        null,
        undefined,
        { cause: error },
      );
    }
    throw new BackendApiError(
      usesLocalBackend()
        ? "Could not reach the local Qotta backend. Start Debug Qotta Backend and try again."
        : "Could not reach the Qotta backend. Please try again.",
      "network_error",
      clientRequestId,
      null,
      undefined,
      { cause: error },
    );
  } finally {
    globalThis.clearTimeout(timeout);
  }
}

export async function initialWebsiteAnalysis(
  websiteUrl: string,
): Promise<BackendSuccess<InitialWebsiteAnalysisResult>> {
  const response = await postBackend("initial_website_analysis", { websiteUrl: websiteUrl.trim() });
  const parsed = initialWebsiteAnalysisResultSchema.safeParse(response.data);
  if (!parsed.success) {
    throw new BackendApiError(
      "The Qotta backend returned an unexpected onboarding result.",
      "invalid_response",
      response.request_id,
      null,
      parsed.error.issues,
    );
  }
  return { ...response, data: parsed.data };
}

export async function updateContext(context: JourneyContext, instructions: string[], trace?: BackendTrace) {
  const response = await postBackend("update_context", { context, instructions }, trace);
  return { ...response, data: { context: contextSchema.parse(response.data["context"]) } };
}

export async function updateDiscovery(context: JourneyContext, model: ExecutableDiscoveryModel, instructions: string[], trace?: BackendTrace) {
  const response = await postBackend("update_discovery", { context, model, instructions }, trace);
  return { ...response, data: { model: validateDiscoveryModel(response.data["model"]) } };
}

export async function sendLeadEmail(
  customerId: string,
  leadId: string,
): Promise<BackendSuccess<LeadEmailResult>> {
  const response = await postBackend("send_email", { template: "new_lead", customerId, leadId });
  const parsed = leadEmailResultSchema.safeParse(response.data);
  if (!parsed.success) {
    throw new BackendApiError(
      "The Qotta backend returned an unexpected lead notification result.",
      "invalid_response",
      response.request_id,
      null,
    );
  }
  return { ...response, data: parsed.data };
}

/** Admin operations always send a verified Firebase identity to the backend. */
export async function postAdminBackend(operation: "admin_management" | "send_email", payload: Record<string, unknown>) {
  const { auth } = await import("@/lib/firebase-auth");
  await auth.authStateReady();
  const user = auth.currentUser;
  if (!user || user.isAnonymous) throw new Error("Sign in as System Admin to continue.");
  const token = await user.getIdToken();
  if (auth.currentUser?.uid !== user.uid) throw new Error("The signed-in user changed.");
  return postBackend(operation, payload, undefined, token);
}
